import { useState, useRef } from 'react'
import './TileDownloader.css'

interface TileDownloaderProps {
  mapCenter: [number, number]
  mapZoom: number
}

function TileDownloader({ mapCenter, mapZoom: _mapZoom }: TileDownloaderProps) {
  const [isDownloading, setIsDownloading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [totalTiles, setTotalTiles] = useState(0)
  const [downloadedTiles, setDownloadedTiles] = useState(0)
  const [downloadedMB, setDownloadedMB] = useState(0)
  const [estimatedMB, setEstimatedMB] = useState(0)
  const cancelDownload = useRef(false)

  // Calculate tile coordinates from lat/lon/zoom
  const latLonToTile = (lat: number, lon: number, zoom: number) => {
    const x = Math.floor(((lon + 180) / 360) * Math.pow(2, zoom))
    const y = Math.floor(
      ((1 -
        Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) /
          Math.PI) /
        2) *
        Math.pow(2, zoom)
    )
    return { x, y, zoom }
  }

  // Download tiles in a bounding box around current location
  const downloadTiles = async (radiusKm: number = 10, minZoom: number = 10, maxZoom: number = 16) => {
    setIsDownloading(true)
    setDownloadedTiles(0)

    const [centerLon, centerLat] = mapCenter
    
    // Approximate: 1 degree lat = 111km, 1 degree lon = 111km * cos(lat)
    const latRadius = radiusKm / 111
    const lonRadius = radiusKm / (111 * Math.cos((centerLat * Math.PI) / 180))

    const bounds = {
      north: centerLat + latRadius,
      south: centerLat - latRadius,
      east: centerLon + lonRadius,
      west: centerLon - lonRadius,
    }

    let tilesToDownload: Array<{ url: string; x: number; y: number; z: number }> = []

    // Calculate all tiles needed
    for (let z = minZoom; z <= maxZoom; z++) {
      const nw = latLonToTile(bounds.north, bounds.west, z)
      const se = latLonToTile(bounds.south, bounds.east, z)

      for (let x = nw.x; x <= se.x; x++) {
        for (let y = nw.y; y <= se.y; y++) {
          // OSM tiles
          tilesToDownload.push({
            url: `https://a.tile.openstreetmap.org/${z}/${x}/${y}.png`,
            x,
            y,
            z,
          })
          
          // NIWA bathymetry tiles (using Web Mercator bbox)
          const bbox = tileToWebMercatorBbox(x, y, z)
          tilesToDownload.push({
            url: `https://atlas.niwa.co.nz/server/rest/services/Bathymetry/NZ_Bathymetry_2016/ImageServer/exportImage?bbox=${bbox}&bboxSR=3857&imageSR=3857&size=256,256&format=png&transparent=true&f=image`,
            x,
            y,
            z,
          })
        }
      }
    }

    setTotalTiles(tilesToDownload.length)
    
    // Estimate total size (OSM tiles ~30KB, NIWA tiles ~100KB average)
    const estimatedSize = tilesToDownload.length * 0.065 // Average 65KB per tile
    setEstimatedMB(estimatedSize)
    
    // Reset cancellation flag
    cancelDownload.current = false

    // Download tiles in batches to avoid overwhelming the browser
    const batchSize = 10
    let downloaded = 0
    let totalBytes = 0

    for (let i = 0; i < tilesToDownload.length; i += batchSize) {
      // Check if user cancelled
      if (cancelDownload.current) {
        console.log('Download cancelled by user')
        setIsDownloading(false)
        return
      }
      
      const batch = tilesToDownload.slice(i, i + batchSize)
      
      await Promise.allSettled(
        batch.map(async (tile) => {
          try {
            const response = await fetch(tile.url)
            if (response.ok) {
              const blob = await response.blob()
              totalBytes += blob.size
              console.log(`Downloaded tile: ${tile.z}/${tile.x}/${tile.y} (${(blob.size / 1024).toFixed(1)}KB)`)
              return blob.size
            }
            return 0
          } catch (error) {
            console.error(`Failed to download tile: ${tile.z}/${tile.x}/${tile.y}`, error)
            return 0
          }
        })
      )

      downloaded += batch.length
      setDownloadedTiles(downloaded)
      setProgress(Math.round((downloaded / tilesToDownload.length) * 100))
      setDownloadedMB(totalBytes / (1024 * 1024)) // Convert to MB
      
      // Small delay between batches
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    setIsDownloading(false)
  }
  
  const handleCancelDownload = () => {
    if (confirm('Cancel download? Downloaded tiles will be kept.')) {
      cancelDownload.current = true
    }
  }

  // Convert tile coordinates to Web Mercator bounding box
  const tileToWebMercatorBbox = (x: number, y: number, z: number): string => {
    const n = Math.pow(2, z)
    const lon1 = (x / n) * 360 - 180
    const lon2 = ((x + 1) / n) * 360 - 180
    
    const lat1Rad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)))
    const lat2Rad = Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n)))
    
    const lat1 = (lat1Rad * 180) / Math.PI
    const lat2 = (lat2Rad * 180) / Math.PI

    // Convert to Web Mercator (EPSG:3857)
    const toWebMercator = (lon: number, lat: number) => {
      const x = (lon * 20037508.34) / 180
      let y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180)
      y = (y * 20037508.34) / 180
      return { x, y }
    }

    const p1 = toWebMercator(lon1, lat1)
    const p2 = toWebMercator(lon2, lat2)

    return `${p1.x},${p2.y},${p2.x},${p1.y}`
  }

  return (
    <div className="tile-downloader">
      <h3>📥 Download Offline Maps</h3>
      <p>Pre-download map tiles for offline use. Great before heading offshore!</p>
      
      <div className="download-options">
        <button 
          onClick={() => downloadTiles(5, 10, 14)} 
          disabled={isDownloading}
          className="download-btn"
        >
          Small Area (5km, Zoom 10-14)
        </button>
        
        <button 
          onClick={() => downloadTiles(10, 10, 15)} 
          disabled={isDownloading}
          className="download-btn"
        >
          Medium Area (10km, Zoom 10-15)
        </button>
        
        <button 
          onClick={() => downloadTiles(20, 10, 16)} 
          disabled={isDownloading}
          className="download-btn"
        >
          Large Area (20km, Zoom 10-16) ⚠️ Slow
        </button>
      </div>

      {isDownloading && (
        <div className="download-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="progress-stats">
            <p>
              <strong>{downloadedTiles} / {totalTiles} tiles</strong> ({progress}%)
            </p>
            <p className="mb-counter">
              <strong>{downloadedMB.toFixed(1)} MB</strong> downloaded
              {estimatedMB > 0 && ` / ~${estimatedMB.toFixed(1)} MB estimated`}
            </p>
          </div>
          <button 
            onClick={handleCancelDownload}
            className="cancel-btn"
          >
            ❌ Cancel Download
          </button>
          <p className="cancel-hint">
            Downloaded tiles will be kept if you cancel
          </p>
        </div>
      )}

      <div className="cache-info">
        <p className="hint">
          💡 <strong>Tip:</strong> Pan map to area you want, then download.<br/>
          📊 Watch the MB counter - cancel and zoom in if too large!
        </p>
        <p className="hint">
          📱 <strong>Storage:</strong> Small (5km) ≈ 20-50 MB | Medium (10km) ≈ 60-150 MB | Large (20km) ≈ 300-500 MB
        </p>
      </div>
    </div>
  )
}

export default TileDownloader
